import { useCallback, useEffect, useRef, useState } from "react";
import { Search, Upload, FileText, Image as ImageIcon, Trash2, Eye, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { ProfileSelect, useProfiles, profileName } from "@/components/ProfileSelect";
import { api, errMsg, fmtDate, openFile, CATEGORIES } from "@/lib/api";
import { toast } from "sonner";

const UploadDialog = ({ open, onClose, onDone, profiles }) => {
  const [f, setF] = useState({ title: "", category: "Altro", profile: "me", tags: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", f.title);
    fd.append("category", f.category);
    fd.append("profile_id", f.profile === "me" ? "" : f.profile);
    fd.append("tags", f.tags);
    setBusy(true);
    try {
      await api.post("/documents", fd);
      toast.success("Documento archiviato");
      onDone();
      onClose();
      setFile(null);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="upload-dialog">
        <DialogHeader><DialogTitle>Archivia un documento</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <Input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => setFile(e.target.files[0])} data-testid="upload-file-input" />
          <div><Label>Titolo</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="es. Contratto affitto 2026" data-testid="upload-title-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Categoria</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="upload-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Di chi</Label><ProfileSelect value={f.profile} onChange={(v) => setF({ ...f, profile: v })} profiles={profiles} testId="upload-profile-select" /></div>
          </div>
          <div><Label>Tag (separati da virgola)</Label><Input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} data-testid="upload-tags-input" /></div>
        </div>
        <DialogFooter><Button disabled={!file || busy} onClick={submit} data-testid="upload-submit-btn">{busy ? "Caricamento…" : "Archivia"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Archive() {
  const profiles = useProfiles();
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [prof, setProf] = useState("all");
  const [open, setOpen] = useState(false);
  const t = useRef();
  const reqId = useRef(0);

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (cat) params.category = cat;
    if (prof !== "all") params.profile_id = prof;
    const id = ++reqId.current;
    api.get("/documents", { params }).then(({ data }) => {
      if (id === reqId.current) setDocs(data);
    });
  }, [q, cat, prof]);
  useEffect(() => {
    clearTimeout(t.current);
    t.current = setTimeout(load, 250);
  }, [load]);

  const remove = async (d) => {
    if (!window.confirm(`Eliminare "${d.title}"?`)) return;
    await api.delete(`/documents/${d.doc_id}`);
    toast.success("Documento eliminato");
    load();
  };

  return (
    <div data-testid="archive-page">
      <PageHeader eyebrow="Archivio" title="I tuoi documenti" desc="Cerca per titolo, tag o contenuto delle analisi.">
        <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="archive-upload-btn"><Upload className="h-4 w-4 mr-2" /> Carica</Button>
      </PageHeader>
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nei documenti…" className="pl-9 bg-white h-11" data-testid="archive-search-input" />
        </div>
        <ProfileSelect value={prof} onChange={setProf} profiles={profiles} allowAll testId="archive-profile-filter" />
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {["", ...CATEGORIES].map((c) => (
          <button key={c || "all"} onClick={() => setCat(c)} data-testid={`archive-cat-${c || "all"}`}
            className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${cat === c ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white hover:bg-slate-50"}`}>{c || "Tutte"}</button>
        ))}
      </div>
      {docs.length === 0 && <div className="rounded-3xl border bg-white p-12 text-center text-slate-500" data-testid="archive-empty">Nessun documento trovato.</div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {docs.map((d) => (
          <div key={d.doc_id} className="lift rounded-2xl border bg-white p-5 flex flex-col" data-testid={`doc-card-${d.doc_id}`}>
            <div className="flex items-start gap-3">
              <span className="h-10 w-10 shrink-0 rounded-xl bg-slate-100 grid place-items-center">{d.content_type === "application/pdf" ? <FileText className="h-5 w-5 text-[#1E3A8A]" /> : <ImageIcon className="h-5 w-5 text-[#E07A5F]" />}</span>
              <div className="min-w-0"><p className="font-medium truncate">{d.title}</p><p className="text-xs text-slate-500">{d.category} · {profileName(profiles, d.profile_id)} · {fmtDate(d.created_at)}</p></div>
            </div>
            {d.analysis?.riassunto && <p className="mt-3 text-sm text-slate-600 line-clamp-3 flex gap-1"><Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#E07A5F]" />{d.analysis.riassunto}</p>}
            {d.tags?.filter(Boolean).length > 0 && <div className="mt-3 flex flex-wrap gap-1">{d.tags.filter(Boolean).map((t) => <span key={t} className="text-[11px] rounded-full bg-slate-100 px-2 py-0.5">{t}</span>)}</div>}
            <div className="mt-auto pt-4 flex gap-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => openFile(`/documents/${d.doc_id}/file`).catch(() => toast.error("Apertura non riuscita"))} data-testid={`doc-open-${d.doc_id}`}><Eye className="h-4 w-4 mr-1" /> Apri</Button>
              <Button size="sm" variant="ghost" className="rounded-full text-red-600" onClick={() => remove(d)} data-testid={`doc-delete-${d.doc_id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      <UploadDialog open={open} onClose={() => setOpen(false)} onDone={load} profiles={profiles} />
    </div>
  );
}
